import * as Sentry from '@sentry/browser';
import { useQuery } from '@tanstack/react-query';
import { UseQueryResult } from '@tanstack/react-query/src/types';
import React, { createContext, useContext } from 'react';
import packagejson from '../../../package.json';
import { getApplicationContext } from '../../api/application';
import { EnvironmentDTO } from '../../api/dto';

export const ENVIRONMENT_SLUGS = {
    LOCAL: 'local',
    DEVELOPMENT: 'dev',
    TEST: 'test',
    PRODUCTION: 'prod',
} as const satisfies Record<EnvironmentDTO, string>;

export const ApplicationContext = createContext<
    UseQueryResult<Awaited<ReturnType<typeof getApplicationContext>>> | undefined
>(undefined);

export const useAsyncApplicationContext = () => {
    const value = useContext(ApplicationContext);
    if (value === undefined) {
        throw new Error(); // Out of context
    }
    return value;
};

export const useApplicationContext = () => {
    const { data: context } = useAsyncApplicationContext();
    if (context === undefined) {
        throw new Error(); // Context is undefined
    }
    return context;
};

interface ApplicationContextProviderProps {
    children: React.ReactNode;
}
export const ApplicationContextProvider: React.FC<ApplicationContextProviderProps> = ({ children }) => {
    const frontendVersion = packagejson.version;

    const usedQuery = useQuery(['application-context'], () => getApplicationContext(), {
        onSuccess: (data) => {
            if (data.frontendSentryDSN) {
                Sentry.init({
                    dsn: data.frontendSentryDSN,
                    environment: data.environment.toLowerCase(),
                    release: frontendVersion,
                });
            }
        },
    });

    return <ApplicationContext.Provider value={usedQuery}>{children}</ApplicationContext.Provider>;
};
